import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, User, Brain } from 'lucide-react';

const FormField = ({ 
  icon: Icon, 
  type, 
  name, 
  value, 
  onChange, 
  placeholder, 
  required, 
  showPassword, 
  setShowPassword 
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {name.charAt(0).toUpperCase() + name.slice(1)}
    </label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
      <input
        type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
        placeholder={placeholder}
        required={required}
        minLength={type === 'password' ? 6 : undefined}
      />
      {type === 'password' && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 pt-4 text-gray-400 hover:text-gray-600"
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
    </div>
  </div>
);

//   <button
//     onClick={onClick}
//     disabled={disabled}
//     className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
//   >
//     <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
//       <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
//       <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
//       <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
//       <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
//     </svg>
//     Continue with Google
//   </button>
// );

const AuthHeader = ({ isLogin }) => (
  <div className="text-center mb-8">
    <div className="flex items-center justify-center mb-4">
      <Brain className="w-10 h-10 text-blue-600 mr-3" />
      <h1 className="text-2xl font-bold text-gray-900">Smart Task Manager</h1>
    </div>
    <p className="text-gray-600">
      {isLogin ? 'Welcome back! Sign in to continue' : 'Create your account to get started'}
    </p>
  </div>
);

const ErrorMessage = ({ error }) => error && (
  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
    <p className="text-sm">{error}</p>
  </div>
);

const SubmitButton = ({ loading, isLogin }) => (
  <button
    type="submit"
    disabled={loading}
    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
  >
    {loading ? (
      <>
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
        {isLogin ? 'Signing In...' : 'Creating Account...'}
      </>
    ) : (
      <>
        <Brain className="w-5 h-5 mr-2" />
        {isLogin ? 'Sign In' : 'Create Account'}
      </>
    )}
  </button>
);

const ToggleAuth = ({ isLogin, onToggle }) => (
  <div className="text-center mt-6">
    <p className="text-sm text-gray-600">
      {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
      <button
        onClick={onToggle}
        className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
      >
        {isLogin ? 'Sign up' : 'Sign in'}
      </button>
    </p>
  </div>
);

const AuthWrapper = ({ children }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { currentUser, signin, signup, signInWithGoogle } = useAuth();

  if (currentUser) return children;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await signin(formData.email, formData.password);
        // Reset form after successful login
        setFormData({
          email: '',
          password: '',
          name: '',
          confirmPassword: ''
        });
        setShowPassword(false);
      } else {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        await signup(formData.email, formData.password, formData.name);
        // Reset form after successful signup
        setFormData({
          email: '',
          password: '',
          name: '',
          confirmPassword: ''
        });
        setShowPassword(false);
      }
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const resetForm = () => {
    setIsLogin(!isLogin);
    setError('');
    setFormData({
      email: '',
      password: '',
      name: '',
      confirmPassword: ''
    });
    setShowPassword(false);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
          <AuthHeader isLogin={isLogin} />
          <ErrorMessage error={error} />

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <FormField
                icon={User}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="John Doe"
                required={!isLogin}
              />
            )}

            <FormField
              icon={Mail}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your@email.com"
              required
            />

            <FormField
              icon={Lock}
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="••••••••"
              required
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />

            {!isLogin && (
              <FormField
                icon={Lock}
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="••••••••"
                required={!isLogin}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
              />
            )}

            <SubmitButton loading={loading} isLogin={isLogin} />
          </form>

          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-sm text-gray-500">or</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* <GoogleSignInButton onClick={handleGoogleSignIn} disabled={loading} /> */}
          <ToggleAuth isLogin={isLogin} onToggle={resetForm} />
        </div>
      </div>
    </div>
  );
};

export default AuthWrapper;